// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Select,
  Badge,
  ActionIcon,
  SimpleGrid,
  Tabs,
  Loader,
  Center,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DateInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconEdit,
  IconBriefcase,
  IconUser,
  IconCalendar,
  IconSend,
  IconPlayerPause,
  IconX,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable, type Column } from '../../components/DataTable';
import {
  useJobPostings,
  useCreateJobPosting,
  useUpdateJobPosting,
  useCloseJobPosting,
} from '../../hooks/useRecruitment';
import type {
  JobPosting,
  JobPostingType,
  JobPostingStatus,
  CreateJobPostingInput,
} from '../../types/recruitment';

const JOB_TYPE_OPTIONS: { value: JobPostingType; label: string }[] = [
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'TEMPORARY', label: 'Temporary' },
  { value: 'INTERN', label: 'Intern' },
];

const JOB_TYPE_COLORS: Record<string, string> = {
  FULL_TIME: 'blue',
  PART_TIME: 'teal',
  CONTRACT: 'violet',
  TEMPORARY: 'orange',
  INTERN: 'cyan',
};

const STATUS_TABS: { value: string; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'OPEN', label: 'Open' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'CLOSED', label: 'Closed' },
];

interface FormState {
  title: string;
  department: string;
  location: string;
  type: JobPostingType;
  description: string;
  requirements: string[];
  preferredQualifications: string[];
  salaryMin: number | undefined;
  salaryMax: number | undefined;
  currency: string;
  hiringManagerId: string;
  closingDate: string | null;
}

const EMPTY_FORM: FormState = {
  title: '',
  department: '',
  location: '',
  type: 'FULL_TIME',
  description: '',
  requirements: [''],
  preferredQualifications: [''],
  salaryMin: undefined,
  salaryMax: undefined,
  currency: 'USD',
  hiringManagerId: '',
  closingDate: null,
};

export default function JobPostingListPage() {
  const navigate = useNavigate();
  const [statusTab, setStatusTab] = useState('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [newRequirement, setNewRequirement] = useState('');
  const [newPreferred, setNewPreferred] = useState('');

  const queryStatus = statusTab === 'ALL' ? undefined : statusTab;
  const { data: postings, loading: isLoading } = useJobPostings({ status: queryStatus });
  const createMutation = useCreateJobPosting();
  const updateMutation = useUpdateJobPosting();
  const closeMutation = useCloseJobPosting();

  // ── Stats ───────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!postings) return { open: 0, totalApplicants: 0, interviewsThisWeek: 0, pendingOffers: 0 };
    const open = postings.filter((p) => p.status === 'OPEN').length;
    const totalApplicants = postings.reduce((sum, p) => sum + p.applicantCount, 0);
    return { open, totalApplicants, interviewsThisWeek: 0, pendingOffers: 0 };
  }, [postings]);

  // ── Modal Helpers ───────────────────────────────────────────────────
  const openCreateModal = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setNewRequirement('');
    setNewPreferred('');
    setModalOpen(true);
  };

  const openEditModal = (posting: JobPosting) => {
    setEditingId(posting.id);
    setForm({
      title: posting.title,
      department: posting.department,
      location: posting.location,
      type: posting.type,
      description: posting.description,
      requirements: posting.requirements.length > 0 ? posting.requirements : [''],
      preferredQualifications:
        posting.preferredQualifications.length > 0 ? posting.preferredQualifications : [''],
      salaryMin: posting.salaryMin,
      salaryMax: posting.salaryMax,
      currency: posting.currency,
      hiringManagerId: posting.hiringManagerId,
      closingDate: posting.closingDate ?? null,
    });
    setNewRequirement('');
    setNewPreferred('');
    setModalOpen(true);
  };

  const addRequirement = () => {
    const trimmed = newRequirement.trim();
    if (trimmed) {
      setForm((f) => ({ ...f, requirements: [...f.requirements.filter(Boolean), trimmed] }));
      setNewRequirement('');
    }
  };

  const removeRequirement = (index: number) => {
    setForm((f) => ({ ...f, requirements: f.requirements.filter((_: any, i) => i !== index) }));
  };

  const addPreferred = () => {
    const trimmed = newPreferred.trim();
    if (trimmed) {
      setForm((f) => ({
        ...f,
        preferredQualifications: [...f.preferredQualifications.filter(Boolean), trimmed],
      }));
      setNewPreferred('');
    }
  };

  const removePreferred = (index: number) => {
    setForm((f) => ({
      ...f,
      preferredQualifications: f.preferredQualifications.filter((_: any, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    const input: CreateJobPostingInput = {
      title: form.title,
      department: form.department,
      location: form.location,
      type: form.type,
      description: form.description,
      requirements: form.requirements.filter(Boolean),
      preferredQualifications: form.preferredQualifications.filter(Boolean),
      salaryMin: form.salaryMin,
      salaryMax: form.salaryMax,
      currency: form.currency,
      hiringManagerId: form.hiringManagerId,
      closingDate: form.closingDate ?? undefined,
    };

    try {
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, ...input });
        notifications.show({ title: 'Updated', message: 'Job posting updated successfully', color: 'green' });
      } else {
        await createMutation.create(input as any);
        notifications.show({ title: 'Created', message: 'Job posting created successfully', color: 'green' });
      }
      setModalOpen(false);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save job posting',
        color: 'red',
      });
    }
  };

  const handlePublish = async (posting: JobPosting) => {
    try {
      await updateMutation.mutateAsync({ id: posting.id, status: 'OPEN' as JobPostingStatus });
      notifications.show({ title: 'Published', message: `"${posting.title}" is now open`, color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to publish job posting', color: 'red' });
    }
  };

  const handleHold = async (posting: JobPosting) => {
    try {
      await updateMutation.mutateAsync({ id: posting.id, status: 'ON_HOLD' as JobPostingStatus });
      notifications.show({ title: 'On Hold', message: `"${posting.title}" is on hold`, color: 'yellow' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to pause job posting', color: 'red' });
    }
  };

  const handleClose = async (posting: JobPosting) => {
    try {
      await closeMutation.mutateAsync({ id: posting.id, reason: 'Manually closed' });
      notifications.show({ title: 'Closed', message: `"${posting.title}" has been closed`, color: 'gray' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to close job posting', color: 'red' });
    }
  };

  // ── Table Columns ─────────────────────────────────────────────────
  const columns: Column<JobPosting>[] = [
    { key: 'title', label: 'Title', sortable: true },
    { key: 'department', label: 'Department', sortable: true },
    { key: 'location', label: 'Location', sortable: true },
    {
      key: 'type',
      label: 'Type',
      width: 120,
      render: (row) => (
        <Badge color={JOB_TYPE_COLORS[row.type] || 'gray'} variant="light" size="sm">
          {row.type.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'applicantCount',
      label: 'Applicants',
      width: 100,
      sortable: true,
      render: (row) => (
        <Text fw={500}>{row.applicantCount}</Text>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 110,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'postedAt',
      label: 'Posted',
      width: 110,
      sortable: true,
      render: (row) => (row.postedAt ? new Date(row.postedAt).toLocaleDateString() : '--'),
    },
  ];

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Recruitment"
        subtitle={postings ? `${postings.length} job postings` : undefined}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={openCreateModal}>
            New Job Posting
          </Button>
        }
      />

      {/* Summary Cards */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Open Positions"
          value={stats.open}
          icon={<IconBriefcase size={24} />}
          color="blue"
        />
        <StatCard
          title="Total Applicants"
          value={stats.totalApplicants}
          icon={<IconUser size={24} />}
          color="teal"
        />
        <StatCard
          title="Interviews This Week"
          value={stats.interviewsThisWeek}
          icon={<IconCalendar size={24} />}
          color="violet"
        />
        <StatCard
          title="Pending Offers"
          value={stats.pendingOffers}
          icon={<IconSend size={24} />}
          color="orange"
        />
      </SimpleGrid>

      {/* Status Tabs */}
      <Tabs value={statusTab} onChange={(v) => setStatusTab(v || 'ALL')}>
        <Tabs.List>
          {STATUS_TABS.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      {/* Table */}
      <DataTable<JobPosting>
        columns={columns}
        data={postings ?? []}
        loading={isLoading}
        onRowClick={(row) => navigate(`/recruitment/postings/${row.id}`)}
        getRowKey={(row) => row.id}
        emptyMessage="No job postings found"
        actions={(row) => (
          <Group gap={4} wrap="nowrap">
            {row.status === 'DRAFT' && (
              <ActionIcon
                variant="subtle"
                color="green"
                size="sm"
                title="Publish"
                onClick={() => handlePublish(row)}
              >
                <IconSend size={14} />
              </ActionIcon>
            )}
            {row.status === 'OPEN' && (
              <ActionIcon
                variant="subtle"
                color="yellow"
                size="sm"
                title="Put On Hold"
                onClick={() => handleHold(row)}
              >
                <IconPlayerPause size={14} />
              </ActionIcon>
            )}
            <ActionIcon
              variant="subtle"
              color="blue"
              size="sm"
              title="Edit"
              onClick={() => openEditModal(row)}
            >
              <IconEdit size={14} />
            </ActionIcon>
            {row.status !== 'CLOSED' && row.status !== 'FILLED' && (
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                title="Close"
                onClick={() => handleClose(row)}
              >
                <IconX size={14} />
              </ActionIcon>
            )}
          </Group>
        )}
      />

      {/* Create / Edit Modal */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Edit Job Posting' : 'New Job Posting'}
        size="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Title"
            required
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.currentTarget.value }))}
            placeholder="e.g. Senior Software Engineer"
          />

          <Group grow>
            <TextInput
              label="Department"
              required
              value={form.department}
              onChange={(e) => setForm((f) => ({ ...f, department: e.currentTarget.value }))}
              placeholder="e.g. Engineering"
            />
            <TextInput
              label="Location"
              required
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.currentTarget.value }))}
              placeholder="e.g. Remote / New York, NY"
            />
          </Group>

          <Group grow>
            <Select
              label="Type"
              required
              value={form.type}
              onChange={(v) => setForm((f) => ({ ...f, type: (v as JobPostingType) || 'FULL_TIME' }))}
              data={JOB_TYPE_OPTIONS}
            />
            <TextInput
              label="Hiring Manager ID"
              required
              value={form.hiringManagerId}
              onChange={(e) => setForm((f) => ({ ...f, hiringManagerId: e.currentTarget.value }))}
              placeholder="Manager ID"
            />
          </Group>

          <Textarea
            label="Description"
            required
            minRows={4}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.currentTarget.value }))}
            placeholder="Job description..."
          />

          {/* Requirements */}
          <div>
            <Text fw={500} size="sm" mb={4}>
              Requirements
            </Text>
            {form.requirements.filter(Boolean).map((req, i) => (
              <Group key={i} gap="xs" mb={4}>
                <Text size="sm" style={{ flex: 1 }}>
                  {req}
                </Text>
                <ActionIcon variant="subtle" color="red" size="xs" onClick={() => removeRequirement(i)}>
                  <IconX size={12} />
                </ActionIcon>
              </Group>
            ))}
            <Group gap="xs">
              <TextInput
                placeholder="Add requirement..."
                value={newRequirement}
                onChange={(e) => setNewRequirement(e.currentTarget.value)}
                onKeyDown={(e) => e.key === 'Enter' && addRequirement()}
                style={{ flex: 1 }}
                size="xs"
              />
              <Button size="xs" variant="light" onClick={addRequirement}>
                Add
              </Button>
            </Group>
          </div>

          {/* Preferred Qualifications */}
          <div>
            <Text fw={500} size="sm" mb={4}>
              Preferred Qualifications
            </Text>
            {form.preferredQualifications.filter(Boolean).map((pq, i) => (
              <Group key={i} gap="xs" mb={4}>
                <Text size="sm" style={{ flex: 1 }}>
                  {pq}
                </Text>
                <ActionIcon variant="subtle" color="red" size="xs" onClick={() => removePreferred(i)}>
                  <IconX size={12} />
                </ActionIcon>
              </Group>
            ))}
            <Group gap="xs">
              <TextInput
                placeholder="Add preferred qualification..."
                value={newPreferred}
                onChange={(e) => setNewPreferred(e.currentTarget.value)}
                onKeyDown={(e) => e.key === 'Enter' && addPreferred()}
                style={{ flex: 1 }}
                size="xs"
              />
              <Button size="xs" variant="light" onClick={addPreferred}>
                Add
              </Button>
            </Group>
          </div>

          {/* Salary Range */}
          <Group grow>
            <NumberInput
              label="Salary Min"
              value={form.salaryMin}
              onChange={(v) => setForm((f) => ({ ...f, salaryMin: typeof v === 'number' ? v : undefined }))}
              prefix="$"
              thousandSeparator=","
              min={0}
            />
            <NumberInput
              label="Salary Max"
              value={form.salaryMax}
              onChange={(v) => setForm((f) => ({ ...f, salaryMax: typeof v === 'number' ? v : undefined }))}
              prefix="$"
              thousandSeparator=","
              min={0}
            />
          </Group>

          <DateInput
            label="Closing Date"
            value={form.closingDate}
            onChange={(v) => setForm((f) => ({ ...f, closingDate: v }))}
            placeholder="Optional"
            clearable
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={createMutation.loading || updateMutation.isPending}
              disabled={!form.title || !form.department || !form.location || !form.description}
            >
              {editingId ? 'Update' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
